import { createContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import axiosInstance, { setupInterceptors } from "../api/axiosInstance"; // Adjust path if needed

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("accessToken");
    return token ? { token, user: null } : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refresh the access token
  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem("refreshToken");
    if (!refresh) {
      handleLogout("Session expired. Please login again.");
      throw new Error("No refresh token found.");
    }
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/auth/token/refresh/`, {
        refresh,
      });
  
      const newAccessToken = response.data.access;
      localStorage.setItem("accessToken", newAccessToken);
      setAuth((prev) => ({ ...prev, token: newAccessToken }));
      return newAccessToken;
    } catch (err) {
      handleLogout("Session expired. Please login again.");
      throw err;
    }
  }, []);
  

  const handleLogout = (message = null) => {
    // Clear all auth-related data
    const keysToRemove = [
      "accessToken",
      "refreshToken",
      "userAreas",
      "userAreasTimestamp",
      "selectedAreas",
      "selectedArea"
    ];
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    setAuth(null);
    
    // Only set error and redirect if there's a message (session expiry, etc.)
    if (message) {
      setError(message);
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    } else {
      // Normal logout - just redirect without error
      window.location.href = '/login';
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get("/api/v1/auth/profile/");
      
      // Check if user is active
      if (!res.data.is_active) {
        console.error("User account is inactive");
        handleLogout("Your account has been deactivated. Please contact support.");
        return;
      }
      setAuth((prev) => ({ ...prev, user: res.data }));
    } catch (err) {
      console.error("Failed to fetch user:", err);
      const errorMessage = err.response?.data?.detail || "Failed to fetch user profile";
      handleLogout(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const login = async (access, refresh, user) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!access || !refresh) {
        console.error("Missing tokens:", { access, refresh });
        setError("Invalid login response. Please try again.");
        return false;
      }

      // Store tokens first
      try {
        localStorage.setItem("accessToken", access);
        localStorage.setItem("refreshToken", refresh);
      } catch (storageError) {
        console.error("Failed to store tokens:", storageError);
        setError("Failed to store authentication data. Please try again.");
        return false;
      }
      
      // Fetch user profile to check active status
      try {
        const profileResponse = await axiosInstance.get("/api/v1/auth/profile/");
        const userData = profileResponse.data;
        
        if (!userData) {
          console.error("No user data received from profile");
          throw new Error("Failed to fetch user profile");
        }
        
        // Check if user is active
        const isActive = userData.is_active === true || userData.is_active === 1;
        
        if (!isActive) {
          console.log("User account is inactive");
          setError("Your account has been deactivated. Please contact support.");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          return false;
        }
        
        // Set auth state with user data from profile
        setAuth({ token: access, user: userData });
        return true;
      } catch (profileError) {
        console.error("Profile fetch failed:", profileError);
        // Clean up tokens if profile fetch fails
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setError(profileError.response?.data?.detail || "Failed to fetch user profile. Please try again.");
        return false;
      }
    } catch (err) {
      console.error("Login failed:", err);
      // Clean up tokens if login fails
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      setError(err.response?.data?.detail || "Login failed. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const isAuthenticated = !!auth?.token;
  const isAdmin = auth?.user?.is_staff || false;

  // Fetch user if we already have a token
  useEffect(() => {
    if (auth?.token && !auth.user) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [auth?.token]);

  // Set up interceptors when refreshToken is available
  useEffect(() => {
    setupInterceptors(refreshToken, handleLogout);
  }, [refreshToken]);

  const value = {
    ...auth,
    login,
    logout: handleLogout,
    refreshToken,
    isAuthenticated,
    isAdmin,
    loading,
    error,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
